import { useId } from 'react';

import { useTheme } from '@/theme/useTheme';
import type { ThemePreference } from '@/theme/ThemeContext';
import { Icon, type IconName } from '@/ui/Icon';

const CHOICES: readonly { value: ThemePreference; label: string; icon: IconName }[] = [
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
  { value: 'system', label: 'System', icon: 'system-theme' },
];

/**
 * Chooses the theme.
 *
 * Radio inputs, not toggle buttons. The three options are mutually exclusive,
 * which is what a radio group means, and it buys the arrow-key behaviour and
 * the "2 of 3" announcement for free. The inputs are visually hidden rather
 * than removed, so they remain focusable and the focus ring lands on the label
 * the user is actually looking at.
 *
 * "System" is a first-class option rather than the absence of a choice: a user
 * who wants the app to follow their machine has expressed a preference, and it
 * should survive them opening the app on a light day.
 *
 * @returns the theme control
 */
export function ThemeSwitch(): React.JSX.Element {
  const { preference, setPreference } = useTheme();
  const name = `${useId()}-theme`;

  return (
    <fieldset className="fo-themeswitch">
      <legend className="visually-hidden">Colour theme</legend>
      {CHOICES.map((choice) => {
        const id = `${name}-${choice.value}`;
        return (
          <span key={choice.value} className="fo-themeswitch__choice">
            <input
              className="visually-hidden"
              type="radio"
              id={id}
              name={name}
              value={choice.value}
              checked={preference === choice.value}
              onChange={() => {
                setPreference(choice.value);
              }}
            />
            {/* The label carries a test id because the input it controls is
                clipped to a single pixel: a browser test clicks what a person
                clicks, which is the label. */}
            <label htmlFor={id} title={choice.label} data-testid={`theme-${choice.value}`}>
              <Icon name={choice.icon} size="1.1em" />
              <span className="visually-hidden">{choice.label}</span>
            </label>
          </span>
        );
      })}
    </fieldset>
  );
}
